<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="network-performance-page">
            <!-- 顶部经理信息 -->
            <div class="manager-header">
                <div class="manager-info">
                    <div class="manager-name">
                        <span>{{ userInfo.name }}</span>
                        <span class="manager-id">ID: {{ userInfo.wx_user_code }}</span>
                    </div>
                    <div class="outlet-count">管辖网点: {{ userInfo.outlet_count }}个</div>
                </div>
                <!--  @click="showSemesterPicker = true" 暂不可选择学期 -->
                <van-button type="primary" size="small" plain class="semester-btn">
                    <van-icon name="calendar-o" />
                    {{ selectedSemester.name }}
                </van-button>
            </div>

            <!-- 主要统计卡片 -->
            <div class="stats-section">
                <div class="stats-grid">
                    <div v-for="stat in mainStats" :key="stat.key" :class="`stat-card ${stat.type}`">
                        <div class="stat-title">{{ stat.title }}</div>
                        <div class="stat-value">{{ stat.value }}</div>
                    </div>
                </div>
            </div>

            <!-- 导航标签 -->
            <div class="tab-section">
                <van-tabs v-model:active="activeTab" class="performance-tabs">
                    <van-tab title="套餐订奶统计" name="package">
                        <div class="tab-content">
                            <div class="school-list">
                                <div v-for="school in schoolStats" :key="school.place_name" class="school-item">
                                    <div class="school-header">
                                        <div class="school-name">{{ school.place_name }}</div>
                                        <div class="target-info">
                                            <span class="target-value">目标:{{ school.target_order_rate }}%</span>
                                            <span class="target-label"> / {{ school.target_milk_num }}人</span>
                                        </div>
                                    </div>
                                    <div class="school-details">
                                        <div class="detail-item">
                                            <span class="detail-label">已订人数:</span>
                                            <span class="detail-value subscribed">{{ school.ordered_students }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">订奶率:</span>
                                            <span class="detail-value rate">{{ school.order_rate }}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </van-tab>

                    <van-tab title="刷脸购买统计" name="face">
                        <div class="tab-content">
                            <div class="face-purchase-stats">
                                <div v-for="school in facePurchaseStats" :key="school.place_name" class="face-school-item">
                                    <div class="face-school-name">{{ school.place_name }}</div>
                                    <div class="face-stats-grid">
                                        <div class="face-stat-item">
                                            <div class="face-stat-label">销售数量：</div>
                                            <div class="face-stat-value sales-quantity">{{ school.sales_quantity }}</div>
                                        </div>
                                        <div class="face-stat-item">
                                            <div class="face-stat-label">销售金额：</div>
                                            <div class="face-stat-value sales-amount">¥{{ school.sales_amount }}</div>
                                        </div>
                                        <div class="face-stat-item">
                                            <div class="face-stat-label">已采集人数：</div>
                                            <div class="face-stat-value collected-people">{{ school.face_collected }}
                                            </div>
                                        </div>
                                        <div class="face-stat-item">
                                            <div class="face-stat-label">采集率：</div>
                                            <div class="face-stat-value collection-rate">{{ school.collection_rate }}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </van-tab>
                </van-tabs>
            </div>

            <!-- 学期选择器 -->
            <van-popup v-model:show="showSemesterPicker" position="bottom" round>
                <van-picker :columns="semesterColumns" @confirm="onSemesterConfirm" @cancel="showSemesterPicker = false"
                    title="选择学期" :columns-field-names="customFieldName" />
            </van-popup>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { post } from '@/utils/request'

onMounted(() => {
    setWxTitle('网点业绩')
    initPage()
})

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 响应式数据
const showSemesterPicker = ref(false)
const activeTab = ref('package')
const selectedSemester = ref(null)
const userInfo = ref(null)

// 学期选项
const semesterColumns = ref([])
const customFieldName = {
    text: 'name',
    value: 'id'
}

// 主要统计数据
const mainStats = ref([])

// 学校统计数据
const schoolStats = ref([])

// 刷脸购买统计数据
const facePurchaseStats = ref([])

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 页面初始化
const initPage = async () => {
    try {
        // 这里可以调用API获取网点业绩数据
        const res = await post('/api/outlet/performance/statistics', {
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            partner_id: parseInt(localStorage.getItem('userId')),
        })
        if (res.result && res.result.state === 'ok') {
            semesterColumns.value = res.result.term_list || []
            selectedSemester.value = res.result.selected_term || null
            userInfo.value = res.result.partner_info || null
            const total_statistics = res.result.total_statistics || 0
            // 处理统计数据
            mainStats.value = [
                {
                    key: 'total',
                    title: '总人数',
                    value: total_statistics.total_students,
                    type: 'total'
                },
                {
                    key: 'subscribed',
                    title: '已订人数',
                    value: total_statistics.ordered_students,
                    type: 'subscribed'
                },
                {
                    key: 'amount',
                    title: '总订金额',
                    value: '¥' + total_statistics.total_amount,
                    type: 'amount'
                },
                {
                    key: 'copies',
                    title: '总订份数',
                    value: total_statistics.total_orders,
                    type: 'copies'
                },
                {
                    key: 'rate',
                    title: '订奶率',
                    value: total_statistics.order_rate + '%',
                    type: 'rate'
                },
                {
                    key: 'target',
                    title: '目标完成率',
                    value: total_statistics.target_order_rate + '%',
                    type: 'target'
                },
                {
                    key: 'collected',
                    title: '人脸采集数',
                    value: total_statistics.face_collected,
                    type: 'collected'
                },
                {
                    key: 'uncollected',
                    title: '未采集数量',
                    value: total_statistics.face_not_collected,
                    type: 'uncollected'
                }
            ]
            schoolStats.value = res.result.package_milk_statistics || []
            facePurchaseStats.value = res.result.face_purchase_statistics || []
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }

    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
    }
}

// 方法
const onSemesterConfirm = (value) => {
    selectedSemester.value = value
    showSemesterPicker.value = false
    console.log('选择学期:', value)
    // 这里可以根据选择的学期重新获取数据
    initPage()
}
</script>

<style scoped>
.network-performance-page {
    background: #fff;
    min-height: calc(100vh - 14px);
    padding: 14px;
}

.manager-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px;
    border-radius: 7px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    margin-bottom: 21px;
}

.manager-info {
    flex: 1;
}

.manager-name {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 7px;
}

.manager-id,
.outlet-count {
    font-size: 12px;
    color: #6B7280;
}

.semester-btn {
    font-size: 12px;
    border-radius: 8px;
}

.stats-section {
    margin-bottom: 21px;
}

.stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.stat-card {
    border-radius: 7px;
    padding: 11px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.stat-card.total {
    background: #FEF2F2;
}

.stat-card.subscribed {
    background: #F0FDF4;
}

.stat-card.amount {
    background: #EFF6FF;
}

.stat-card.copies {
    background: #FAF5FF;
}

.stat-card.rate {
    background: #FEFCE8;
}

.stat-card.target {
    background: #EEF2FF;
}

.stat-card.collected {
    background: #F0FDF4;
}

.stat-card.uncollected {
    background: #FEF2F2;
}

.stat-title {
    font-size: 12px;
    color: #6B7280;
    margin-bottom: 10px;
}

.stat-value {
    font-size: 16px;
    font-weight: 500;
}

.stat-card.total .stat-value {
    color: var(--van-primary-color);
}

.stat-card.subscribed .stat-value {
    color: #16A34A;
}

.stat-card.amount .stat-value {
    color: #2563EB;
}

.stat-card.copies .stat-value {
    color: #9333EA;
}

.stat-card.rate .stat-value {
    color: #CA8A04;
}

.stat-card.target .stat-value {
    color: #4F46E5;
}

.stat-card.collected .stat-value {
    color: #16A34A;
}

.stat-card.uncollected .stat-value {
    color: #DC2626;
}

.tab-section {
    margin-bottom: 20px;
}

.performance-tabs {
    background: #fff;
    border-radius: 12px;
}

:deep(.van-tabs__nav) {
    padding: 0;
}

.tab-content {
    padding: 16px 0;
}

.school-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.school-item {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.school-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.school-name {
    font-size: 14px;
    font-weight: 500;
}

.target-info,
.detail-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.target-label,
.detail-label {
    font-size: 12px;
    color: #6B7280;
}

.target-value {
    font-size: 12px;
    color: var(--van-primary-color);
}

.school-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.detail-value {
    font-size: 12px;
    font-weight: 500;
}

.detail-value.subscribed {
    color: var(--van-primary-color);
}

.detail-value.rate {
    color: #16A34A;
}

.face-purchase-stats {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.face-school-item {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.face-school-name {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 10px;
}

.face-stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.face-stat-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.face-stat-label {
    font-size: 12px;
    color: #6B7280;
}

.face-stat-value {
    font-size: 12px;
    font-weight: 500;
}

.face-stat-value.sales-quantity {
    color: var(--van-primary-color);
}

.face-stat-value.sales-amount {
    color: #2563EB;
}

.face-stat-value.collected-people {
    color: #16A34A;
}

.face-stat-value.collection-rate {
    color: #9333EA;
}
</style>